/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useRef, useState } from "react";
import {
  Box,
  ExtendedPaginationBox,
  FlexContainer,
  Input,
  Sidebar,
} from "@nordcloud/gnui";
import {
  SidebarSelectorProps,
  UISidebarProps,
  SidebarItemList,
} from "~/components/SidebarSelector";
import { LoaderWrap } from "~/components/Utils";
import { noop } from "~/tools";

type Props = SidebarSelectorProps &
  UISidebarProps & {
    page: number;
    count: number;
    isLoading: boolean;
    setPage: (page: number) => void;
    setSearch: (search: string) => void;
    excludeItems?: string[];
  };

export function GcpProjectSidebarSelector({
  limit,
  hasIcon,
  name,
  hasSubtitle,
  isOpen,
  page,
  count,
  isLoading,
  options = [],
  onClick,
  onSubmit,
  setPage,
  setSearch,
  excludeItems,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    const inputRefCurrent = inputRef?.current;
    if (isOpen && inputRefCurrent) {
      inputRefCurrent.focus();
    }
  }, [options, isOpen]);

  useEffect(() => {
    const handleSearch = () => {
      setSearch(searchValue);
      setPage(0);
    };
    handleSearch();
  }, [searchValue]);

  const onClose = () => {
    setSearchValue("");
    onClick();
  };

  return (
    <Sidebar
      title={`Select ${name}`}
      isOpen={isOpen}
      footer={
        <ExtendedPaginationBox
          sidebar
          small
          count={count}
          from={page * limit}
          setSize={noop}
          size={limit}
          setPage={(newPage) => {
            setPage(Math.ceil(newPage / limit));
          }}
        />
      }
      onClick={onClose}
    >
      <Box boxStyle="lightGrey">
        <FlexContainer>
          <div css={{ flexGrow: 1 }}>
            <Input
              ref={inputRef}
              small
              value={searchValue}
              type="text"
              placeholder="Search"
              onChange={(event) =>
                setSearchValue(event.target.value.toLowerCase())
              }
            />
          </div>
        </FlexContainer>
      </Box>
      <LoaderWrap inContent loading={isLoading}>
        <SidebarItemList
          items={options}
          hasSubtitle={hasSubtitle}
          hasIcon={hasIcon}
          excludeItems={excludeItems}
          onSubmit={onSubmit}
        />
      </LoaderWrap>
    </Sidebar>
  );
}
